/**
 * Public-browse strategy detail modal. Adapted from
 * packages/dashboard/src/components/StrategyDetail.tsx with the
 * simulated buy state machine + balance-check stripped — the public
 * site has no daemon connection, so the right column is replaced by
 * an InstallCTA that deeplinks to the user's local daemon (or shows
 * the CLI install path when no daemon is running).
 *
 * Trust-badge logic mirrors the grid: the "Verified by Strykr" pill
 * renders ONLY for strategies whose build-time signature pinned to
 * the Strykr pubkey. Self-claimed publishers get a low-trust
 * dashed-border pill instead.
 */
import { useEffect } from "react";
import { Icon } from "./Icon.js";
import { StarRating } from "./StarRating.js";
import { VoteWidget } from "./VoteWidget.js";
import { InstallCTA } from "./InstallCTA.js";
import type { Strategy } from "../lib/types.js";
import type { DaemonStatus } from "../lib/daemon.js";

export interface StrategyDetailDialogProps {
  open: boolean;
  strategy: Strategy | null;
  daemon: DaemonStatus | null;
  onClose: () => void;
}

export function StrategyDetailDialog({
  open,
  strategy,
  daemon,
  onClose,
}: StrategyDetailDialogProps) {
  // ESC closes the modal — common a11y affordance.
  useEffect(() => {
    if (!open) return;
    const handler = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, [open, onClose]);

  if (!open || !strategy) return null;

  const cryptoVerified = strategy.kind === "strykr" && strategy.signerVerified === true;

  return (
    <div className="sd-scrim" onClick={onClose}>
      <div
        className="sd-modal"
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-labelledby="sd-name"
      >
        {/* Header */}
        <div className="sd-head">
          <div
            className="sd-mono"
            style={{ background: strategy.accentHex + "22", color: strategy.accentHex }}
          >
            {strategy.monogram}
          </div>
          <div className="sd-head-body">
            <div className="sd-head-row">
              <h3 id="sd-name">{strategy.name}</h3>
              {cryptoVerified && (
                <span
                  className="sd-verified"
                  title="Cryptographic signature pinned to the Strykr ed25519 pubkey at build time"
                >
                  <Icon name="check" size={10} /> Verified by Strykr
                </span>
              )}
              {strategy.verified && !cryptoVerified && (
                <span
                  className="sd-self-claimed"
                  title="Self-claimed by publisher · not cryptographically verified"
                >
                  Self-claimed
                </span>
              )}
              <span className={"sd-kind " + strategy.kind}>
                {strategy.kind === "strykr" ? "Strykr · Prism" : "Community"}
              </span>
            </div>
            <div className="sd-author">
              by {strategy.author} · updated {humanizeAgo(strategy.updatedAt)}
            </div>
          </div>
          <button onClick={onClose} className="sd-x" aria-label="Close">
            <Icon name="x" size={14} />
          </button>
        </div>

        {/* Body */}
        <div className="sd-body">
          {/* Left column: long copy */}
          <div className="sd-col">
            <div className="sd-tag">{strategy.tagline}</div>

            <div className="sd-section">
              <div className="sd-lab">Description</div>
              <p className="sd-desc">{strategy.description}</p>
            </div>

            <div className="sd-section">
              <div className="sd-lab">Community votes</div>
              <VoteWidget strategyId={strategy.id} variant="detail" />
            </div>

            <div className="sd-section">
              <div className="sd-lab">Performance · last 30 days</div>
              <div className="sd-perf">
                <Stat
                  k="Return"
                  v={fmtPct(strategy.perf.return30dPct, true)}
                  tone={strategy.perf.return30dPct >= 0 ? "pos" : "neg"}
                />
                <Stat k="Sharpe 90d" v={strategy.perf.sharpe90d.toFixed(2)} />
                <Stat k="Max DD" v={fmtPct(strategy.perf.maxDdPct, true)} tone="neg" />
                <Stat k="Trades" v={strategy.perf.trades30d.toLocaleString()} />
                <Stat k="Win rate" v={`${strategy.perf.winRatePct}%`} />
                <Stat k="Max lev" v={`${strategy.maxLeverage}x`} />
              </div>
            </div>

            <div className="sd-section">
              <div className="sd-lab">Targets</div>
              <div className="sd-tags">
                {strategy.assets.map((a) => (
                  <span className="sd-asset" key={a}>
                    {a}
                  </span>
                ))}
                <span className="sd-sep" />
                {strategy.tags.map((t) => (
                  <span className="sd-tag-chip" key={t}>
                    {t}
                  </span>
                ))}
              </div>
            </div>

            <div className="sd-section">
              <div className="sd-lab">Reviews ({strategy.ratings.count.toLocaleString()})</div>
              <div className="sd-reviews">
                <div className="sd-rev-summary">
                  <div className="sd-rev-num">{strategy.ratings.stars.toFixed(2)}</div>
                  <StarRating value={strategy.ratings.stars} size={14} variant="compact" />
                </div>
                <div className="sd-rev-hist">
                  {([5, 4, 3, 2, 1] as const).map((star) => {
                    const c = strategy.ratings.hist[star - 1] ?? 0;
                    const pct = strategy.ratings.count
                      ? Math.round((c / strategy.ratings.count) * 100)
                      : 0;
                    return (
                      <div className="sd-rev-row" key={star}>
                        <span className="lbl">{star}</span>
                        <div className="bar">
                          <div className="fill" style={{ width: pct + "%" }} />
                        </div>
                        <span className="ct">{c.toLocaleString()}</span>
                      </div>
                    );
                  })}
                </div>
              </div>
              <div className="sd-rev-foot">
                Voting + reviews land in v1.2. For now, ratings reflect manual curator scoring.
              </div>
            </div>
          </div>

          {/* Right column: install panel (replaces the simulated x402 buy chain
              from the daemon-side dashboard with a deeplink-or-CLI flow). */}
          <div className="sd-buy">
            <div className="sd-price-box">
              <div className="sd-price-lab">Price</div>
              <div className="sd-price-num">
                {strategy.priceUsdc === 0 ? "Free" : `$${strategy.priceUsdc}`}
              </div>
              <div className="sd-price-sub">
                {strategy.priceUsdc === 0
                  ? "Open access · no payment required"
                  : strategy.oneTimePayment
                    ? "One-time · USDC · lifetime access"
                    : "Per month · USDC · cancel any time"}
              </div>
            </div>

            <div className="sd-install-block">
              <InstallCTA daemon={daemon} strategyId={strategy.id} />
            </div>

            <div className="sd-payment">
              <div className="sd-pay-row">
                <Icon name="lock" size={11} />
                <span>Non-custodial · keys stay on your machine</span>
              </div>
              <div className="sd-pay-row">
                <Icon name="check" size={11} />
                <span>{cryptoVerified ? "Cryptographically signed by Strykr" : "Author signature carried in manifest"}</span>
              </div>
              <div className="sd-pay-row">
                <Icon name="check" size={11} />
                <span>Capability caps enforced by daemon at run time</span>
              </div>
              <div className="sd-pay-row">
                <Icon name="check" size={11} />
                <span>Uninstall any time, no lock-in</span>
              </div>
            </div>
          </div>
        </div>

        <Styles />
      </div>
    </div>
  );
}

function Stat({ k, v, tone }: { k: string; v: string; tone?: "pos" | "neg" }) {
  return (
    <div className="sd-stat">
      <div className="sd-stat-k">{k}</div>
      <div className={"sd-stat-v" + (tone ? " " + tone : "")}>{v}</div>
    </div>
  );
}

function fmtPct(n: number, sign: boolean): string {
  return (sign && n >= 0 ? "+" : "") + n.toFixed(1) + "%";
}

function humanizeAgo(iso: string): string {
  const ms = Date.now() - Date.parse(iso);
  if (ms < 60_000) return "just now";
  if (ms < 3_600_000) return `${Math.floor(ms / 60_000)}m ago`;
  if (ms < 86_400_000) return `${Math.floor(ms / 3_600_000)}h ago`;
  return `${Math.floor(ms / 86_400_000)}d ago`;
}

function Styles() {
  return (
    <style>{`
      .sd-scrim { position: fixed; inset: 0;
        background: rgba(10, 11, 13, 0.62); backdrop-filter: blur(6px);
        display: flex; align-items: center; justify-content: center;
        z-index: 60; padding: 20px;
        animation: at-scrim-in var(--at-dur-med) var(--at-ease); }
      .sd-modal { background: var(--at-surface);
        border: 1px solid var(--at-border); border-radius: var(--at-radius-xl);
        box-shadow: var(--at-shadow-pop);
        width: 880px; max-width: 96vw; max-height: 92vh;
        display: flex; flex-direction: column; overflow: hidden;
        animation: at-modal-in var(--at-dur-med) var(--at-ease-out); }

      .sd-head { display: grid; grid-template-columns: 56px 1fr auto;
        gap: 14px; padding: 18px 22px; align-items: center;
        border-bottom: 1px solid var(--at-border); }
      .sd-mono { width: 56px; height: 56px; border-radius: 12px;
        display: flex; align-items: center; justify-content: center;
        font-family: var(--at-font-mono); font-weight: 700; font-size: 18px; letter-spacing: -0.5px; }
      .sd-head-body { min-width: 0; }
      .sd-head-row { display: flex; align-items: center; gap: 10px; flex-wrap: wrap; }
      .sd-head h3 { margin: 0; font-size: 19px; font-weight: 600; color: var(--at-fg); letter-spacing: -0.3px; }
      .sd-verified { display: inline-flex; align-items: center; gap: 4px;
        font-family: var(--at-font-mono); font-size: 9px; letter-spacing: 0.16em; text-transform: uppercase;
        color: var(--at-accent); padding: 3px 7px; border-radius: var(--at-radius-full);
        background: rgba(74,222,128,0.1); border: 1px solid rgba(74,222,128,0.22); cursor: help; }
      .sd-self-claimed { display: inline-flex; align-items: center; gap: 4px;
        font-family: var(--at-font-mono); font-size: 9px; letter-spacing: 0.16em; text-transform: uppercase;
        color: var(--at-muted); padding: 3px 7px; border-radius: var(--at-radius-full);
        background: var(--at-bg); border: 1px dashed var(--at-hairline); cursor: help; }
      .sd-kind { font-family: var(--at-font-mono); font-size: 9px; letter-spacing: 0.16em; text-transform: uppercase;
        padding: 3px 7px; border-radius: var(--at-radius-full);
        background: var(--at-bg); border: 1px solid var(--at-hairline); color: var(--at-muted); }
      .sd-kind.strykr { color: var(--at-accent); border-color: rgba(74,222,128,0.2); background: rgba(74,222,128,0.06); }
      .sd-author { font-family: var(--at-font-mono); font-size: 11px; color: var(--at-muted); margin-top: 4px; }
      .sd-x { background: transparent; border: 0; color: var(--at-muted); cursor: pointer; padding: 6px;
        border-radius: var(--at-radius-sm); }
      .sd-x:hover { color: var(--at-fg); background: var(--at-bg-hover); }

      .sd-body { display: grid; grid-template-columns: 1fr 320px; gap: 0;
        flex: 1; overflow: hidden; }
      @media (max-width: 720px) { .sd-body { grid-template-columns: 1fr; } }
      .sd-col { padding: 18px 22px; overflow-y: auto; display: flex; flex-direction: column; gap: 18px; }

      .sd-tag { font-size: 14px; color: var(--at-fg); line-height: 1.55; }
      .sd-section { display: flex; flex-direction: column; gap: 10px; }
      .sd-lab { font-family: var(--at-font-mono); font-size: 10px; letter-spacing: 0.18em;
        text-transform: uppercase; color: var(--at-muted); }
      .sd-desc { margin: 0; font-size: 13.5px; color: var(--at-fg-dim); line-height: 1.65; }

      .sd-perf { display: grid; grid-template-columns: repeat(3, 1fr); gap: 1px;
        background: var(--at-hairline);
        border: 1px solid var(--at-hairline); border-radius: var(--at-radius-md); overflow: hidden; }
      .sd-stat { background: var(--at-surface); padding: 10px 12px; }
      .sd-stat-k { font-family: var(--at-font-mono); font-size: 9px;
        letter-spacing: 0.18em; text-transform: uppercase; color: var(--at-muted); margin-bottom: 3px; }
      .sd-stat-v { font-family: var(--at-font-mono); font-size: 14px; font-weight: 500; color: var(--at-fg);
        font-variant-numeric: tabular-nums; }
      .sd-stat-v.pos { color: var(--at-accent); }
      .sd-stat-v.neg { color: var(--at-danger); }

      .sd-tags { display: flex; flex-wrap: wrap; gap: 4px; align-items: center; }
      .sd-asset { font-family: var(--at-font-mono); font-size: 11px;
        padding: 3px 8px; border-radius: var(--at-radius-sm);
        background: rgba(74,222,128,0.06); border: 1px solid rgba(74,222,128,0.18);
        color: var(--at-accent); font-weight: 600; }
      .sd-tag-chip { font-family: var(--at-font-mono); font-size: 11px;
        padding: 3px 8px; border-radius: var(--at-radius-sm);
        background: var(--at-bg); border: 1px solid var(--at-hairline); color: var(--at-muted); }
      .sd-sep { width: 1px; height: 18px; background: var(--at-hairline); margin: 0 4px; }

      .sd-reviews { display: grid; grid-template-columns: 130px 1fr; gap: 16px;
        background: var(--at-bg); border: 1px solid var(--at-hairline);
        border-radius: var(--at-radius-md); padding: 14px; align-items: center; }
      @media (max-width: 540px) { .sd-reviews { grid-template-columns: 1fr; } }
      .sd-rev-summary { display: flex; flex-direction: column; gap: 4px; align-items: flex-start; }
      .sd-rev-num { font-family: var(--at-font-mono); font-size: 32px; font-weight: 700;
        line-height: 1; color: var(--at-fg); font-variant-numeric: tabular-nums; }
      .sd-rev-hist { display: flex; flex-direction: column; gap: 4px; }
      .sd-rev-row { display: grid; grid-template-columns: 14px 1fr 40px; gap: 8px; align-items: center; }
      .sd-rev-row .lbl { font-family: var(--at-font-mono); font-size: 11px; color: var(--at-muted); }
      .sd-rev-row .bar { height: 5px; background: var(--at-hairline); border-radius: 3px; overflow: hidden; }
      .sd-rev-row .bar .fill { height: 100%; background: var(--at-accent); transition: width var(--at-dur-med) var(--at-ease); }
      .sd-rev-row .ct { font-family: var(--at-font-mono); font-size: 10px; color: var(--at-muted); text-align: right;
        font-variant-numeric: tabular-nums; }
      .sd-rev-foot { font-size: 10.5px; color: var(--at-muted); font-style: italic; padding-top: 4px; }

      .sd-buy { padding: 18px 22px; border-left: 1px solid var(--at-border);
        background: linear-gradient(180deg, rgba(74,222,128,0.03), var(--at-surface) 70%);
        display: flex; flex-direction: column; gap: 14px; overflow-y: auto; }
      @media (max-width: 720px) { .sd-buy { border-left: 0; border-top: 1px solid var(--at-border); } }

      .sd-price-box { padding: 14px;
        background: var(--at-bg); border: 1px solid var(--at-hairline);
        border-radius: var(--at-radius-md); }
      .sd-price-lab { font-family: var(--at-font-mono); font-size: 9px; letter-spacing: 0.18em;
        text-transform: uppercase; color: var(--at-muted); margin-bottom: 4px; }
      .sd-price-num { font-family: var(--at-font-mono); font-size: 28px; font-weight: 700;
        color: var(--at-fg); font-variant-numeric: tabular-nums; line-height: 1; }
      .sd-price-sub { font-size: 11px; color: var(--at-muted); margin-top: 6px; line-height: 1.4; }

      .sd-install-block { padding: 12px;
        background: var(--at-surface); border: 1px solid var(--at-hairline);
        border-radius: var(--at-radius-md); }

      .sd-payment { display: flex; flex-direction: column; gap: 6px;
        font-size: 12px; color: var(--at-fg-dim); }
      .sd-pay-row { display: flex; align-items: center; gap: 8px; }
      .sd-pay-row svg { color: var(--at-accent); flex-shrink: 0; }
    `}</style>
  );
}
